import { CourseHearingDerivedStatus, UserProjectStatus } from '@teachur/http-client';

export const NOT_SUBMITTED_LABEL = 'Not submitted';
export const NOT_SCHEDULED_LABEL = 'Not scheduled';

const PROJECT_STATUS_LABELS: Record<UserProjectStatus, string> = {
  [UserProjectStatus.Pending]: 'Submitted',
  [UserProjectStatus.InReview]: 'In review',
  [UserProjectStatus.Approved]: 'Approved',
  [UserProjectStatus.NotYet]: 'Not yet',
};

const HEARING_STATUS_LABELS: Record<CourseHearingDerivedStatus, string> = {
  pending: 'Requested',
  accepted: 'Scheduled',
  cancelled: NOT_SCHEDULED_LABEL,
  approved: 'Passed',
  notYet: 'Not yet passed',
};

export type SnapshotTone = 'success' | 'warning' | 'danger' | 'muted';

const PROJECT_STATUS_TONES: Record<UserProjectStatus, SnapshotTone> = {
  [UserProjectStatus.Pending]: 'warning',
  [UserProjectStatus.InReview]: 'warning',
  [UserProjectStatus.Approved]: 'success',
  [UserProjectStatus.NotYet]: 'danger',
};

const HEARING_STATUS_TONES: Record<CourseHearingDerivedStatus, SnapshotTone> = {
  pending: 'warning',
  accepted: 'warning',
  cancelled: 'muted',
  approved: 'success',
  notYet: 'danger',
};

export const getProjectStatusLabel = (status?: UserProjectStatus): string =>
  status ? PROJECT_STATUS_LABELS[status] : NOT_SUBMITTED_LABEL;

export const getHearingStatusLabel = (status?: CourseHearingDerivedStatus): string =>
  status ? HEARING_STATUS_LABELS[status] : NOT_SCHEDULED_LABEL;

export const getProjectStatusTone = (status?: UserProjectStatus): SnapshotTone =>
  status ? PROJECT_STATUS_TONES[status] : 'muted';

export const getHearingStatusTone = (status?: CourseHearingDerivedStatus): SnapshotTone =>
  status ? HEARING_STATUS_TONES[status] : 'muted';

interface QuizAttemptLike {
  isDraft?: boolean;
  assessments?: { id: number }[];
  answers?: { correct: boolean; assessmentId: number }[];
}

export const getExamPercent = (attempts: QuizAttemptLike[] | undefined): number => {
  const list = attempts ?? [];
  const totalQuestions = Math.max(
    0,
    ...list.map((attempt) => attempt.assessments?.length ?? attempt.answers?.length ?? 0)
  );
  if (!totalQuestions) return 0;
  const correctAssessmentIds = new Set<number>();
  list
    .filter((attempt) => !attempt.isDraft)
    .forEach((attempt) =>
      attempt.answers?.forEach((answer) => {
        if (answer.correct) correctAssessmentIds.add(answer.assessmentId);
      })
    );
  return Math.min(100, Math.round((correctAssessmentIds.size / totalQuestions) * 100));
};

export interface CreditSegments {
  transferred: number;
  newlane: number;
  remaining: number;
}

export const getCreditSegments = (
  transferred: number,
  newlane: number,
  required: number
): CreditSegments => {
  const cappedTransferred = Math.min(transferred, required);
  const cappedNewlane = Math.min(newlane, required - cappedTransferred);
  return {
    transferred: cappedTransferred,
    newlane: cappedNewlane,
    remaining: required - cappedTransferred - cappedNewlane,
  };
};

interface PlanCourseLike {
  transferred: boolean;
  status: string;
  credits: number;
}

interface PlanLike {
  groups: { requiredCredits: number; courses: PlanCourseLike[] }[];
}

export const getPlanCreditSegments = (plan: PlanLike): CreditSegments => {
  const transferred = plan.groups
    .flatMap((group) => group.courses)
    .filter((course) => course.transferred)
    .reduce((sum, course) => sum + course.credits, 0);
  const newlane = plan.groups
    .flatMap((group) => group.courses)
    .filter((course) => !course.transferred && course.status === 'passed')
    .reduce((sum, course) => sum + course.credits, 0);
  const required = plan.groups.reduce((sum, group) => sum + group.requiredCredits, 0);
  return getCreditSegments(transferred, newlane, required);
};

interface DatedCourse {
  status: string;
  isPaused: boolean;
  updatedAt: string;
}

const latestByUpdate = <T extends DatedCourse>(courses: T[]): T | undefined =>
  courses.reduce<T | undefined>(
    (latest, course) =>
      !latest || new Date(course.updatedAt).getTime() > new Date(latest.updatedAt).getTime()
        ? course
        : latest,
    undefined
  );

export const pickLastWorkedCourse = <T extends DatedCourse>(courses: T[]): T | undefined => {
  const workedOn = courses.filter(
    (course) => course.status === 'inProgress' || course.status === 'completed'
  );
  return latestByUpdate(workedOn.filter((course) => !course.isPaused)) ?? latestByUpdate(workedOn);
};

export const countPassedThisMonth = (
  userCourses: { passed: boolean; passedAt?: string }[],
  now: Date
): number =>
  userCourses.filter((userCourse) => {
    if (!userCourse.passed || !userCourse.passedAt) return false;
    const passedAt = new Date(userCourse.passedAt);
    return passedAt.getFullYear() === now.getFullYear() && passedAt.getMonth() === now.getMonth();
  }).length;
